import { useState, useEffect } from "react";
import { DatabaseSections, ModalModes } from "../../../enums/DatabaseEnums";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import {
  AddDocumentsTableEntriesParamsType,
  DatabaseCommonTableProps,
} from "../../../Types/DatabaseTabTypes";
import {
  documentsDeletedTableMap,
  documentsIdMap,
  DocumentsTableConstants,
  documentsValidationSchema,
  getColumns,
} from "./constants";
import TNButton from "../../../reusable-components/TNButton";
import { message, Modal } from "antd";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import styles from "./DocumentsTable.module.scss";
import AddDocumentsForms from "./AddDocumentsForms";
import Form from "../../Form";
import { isEmptyValue } from "../../../utils/emptyStringCheck";
import DeleteConfirmation from "../DeleteConfirmation";
import { deleteFormSchema } from "../constants";
import QuarryDatabaseService from "../../../services/quarryDatabase.service";

const DocumentsTable = (props?: DatabaseCommonTableProps) => {
  const { section, sectionId, isQuarry, blockedJob, isJob } = props;
  const {
    BUTTON: { ADD_DOCUMENT_LABEL },
    MODAL: {
      ADD_DOCUMENT_MODAL_TITLE,
      SUBMIT_BUTTON_LABEL,
      WIDTH,
      EDIT_DOCUMENT_MODAL_TITLE,
    },
    MESSAGES: {
      NO_DOCUMENT_ERROR,
      ADD_DOCUMENT_SUCCESS,
      ADD_DOCUMENT_ERROR,
      DELETE_DOCUMENT_SUCCESS,
      DELETE_DOCUMENT_ERROR,
      EDIT_DOCUMENT_FAILURE,
      EDIT_DOCUMENT_SUCCESS,
    },
  } = DocumentsTableConstants;

  const {
    FetchDocumentsTable,
    AddDatabaseTableEntries,
    DeleteDatabaseTableEntries,
    EditDatabaseTableEntries,
  } = CustomerDatabaseService();
  const { GetQuarryMaterialForDocuments } = QuarryDatabaseService();
  const { data: materialData } = GetQuarryMaterialForDocuments({
    quarryId: sectionId,
    enabled: isQuarry,
  });
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentsVal, setDocumentsVal] =
    useState<AddDocumentsTableEntriesParamsType>({});
  const [fileList, setFileList] = useState([]);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isDeleteDocumentModalOpen, setIsDeleteDocumentModalOpen] =
    useState<boolean>(false);
  const [docId, setDocId] = useState<string>("");
  const [modalMode, setModalMode] = useState<ModalModes>(ModalModes.CREATE);

  const { data, refetch, isFetching } = FetchDocumentsTable({
    section: section,
    section_id: sectionId,
    page: currentPage,
    perPage: pageSize,
  });
  const { mutateAsync: addDocument, isPending: isAddDocumentPending } =
    AddDatabaseTableEntries();
  const { mutateAsync: deleteDocument, isPending: isDeleteDocumentPending } =
    DeleteDatabaseTableEntries();
  const { mutateAsync: editDocument, isPending: isEditDocumentPending } =
    EditDatabaseTableEntries();

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  const handleDocumentModalOpen = () => setIsDocumentModalOpen(true);
  const handleDocumentModalClose = () => setIsDocumentModalOpen(false);
  const handleDeleteDocumentModalOpen = (id?: string) => {
    setDocId(id);
    setIsDeleteDocumentModalOpen(true);
  };
  const handleDeleteDocumentModalClose = () => {
    setDocId("");
    setIsDeleteDocumentModalOpen(false);
  };
  const handleDelete = async (formVal?: object) => {
    await deleteDocument(
      {
        id: docId,
        deletedTable: documentsDeletedTableMap[section],
        notes: formVal?.archiveReason,
        ...(documentsIdMap[section]
          ? { [documentsIdMap[section]]: sectionId }
          : {}),
      },
      {
        onSuccess: () => {
          message.success(DELETE_DOCUMENT_SUCCESS);
          refetch();
          handleDeleteDocumentModalClose();
        },
        onError: () => {
          message.error(DELETE_DOCUMENT_ERROR);
        },
      },
    );
  };
  const handleEdit = (id?: string) => {
    const docData = data?.data?.find((val) => val?.id === id);
    if (docData) {
      const docName = docData?.docAttachment?.split("/")?.at(-1);
      setModalMode(ModalModes.EDIT);
      setDocId(id);
      setDocumentsVal({
        docName: docData?.docName,
        docType: docData?.docType,
        materialId: docData?.materialId,
        description: docData?.description,
      });
      setDocumentUrl(docData?.docAttachment);
      setFileList([
        {
          uid: JSON.stringify(docData?.docAttachment),
          name: docName,
          status: "done",
          url: docData?.docAttachment,
        },
      ]);
      handleDocumentModalOpen();
    } else {
      message.error(EDIT_DOCUMENT_FAILURE);
    }
  };
  const columns = getColumns({
    handleDeleteModalOpen: handleDeleteDocumentModalOpen,
    handleEditNavigate: handleEdit,
    isQuarry: isQuarry,
    blockedJob: blockedJob,
    isJob,
  });

  useEffect(() => {
    refetch();
  }, [pageSize, currentPage]);

  return (
    <>
      <Form validationSchema={deleteFormSchema}>
        {({ control: { handleSubmit } }) => (
          <DeleteConfirmation
            tabType={DatabaseSections.DOCUMENTS}
            deletePending={isDeleteDocumentPending}
            isOpen={isDeleteDocumentModalOpen}
            handleClose={handleDeleteDocumentModalClose}
            submitActionCall={handleSubmit(handleDelete)}
          />
        )}
      </Form>
      <Form
        validationSchema={documentsValidationSchema(isQuarry)}
        values={documentsVal}
        key={JSON.stringify(documentsVal)}
      >
        {({ clearErrors, control: { handleSubmit, _formValues, _reset } }) => {
          const handleAddDocument = async () => {
            if (isEmptyValue(documentUrl)) {
              message.error(NO_DOCUMENT_ERROR);
              return;
            }
            if (modalMode === ModalModes.CREATE) {
              await addDocument(
                {
                  section: section,
                  section_id: sectionId,
                  id: DatabaseSections.DOCUMENTS,
                  ..._formValues,
                  docAttachment: documentUrl,
                },
                {
                  onSuccess: () => {
                    message.success(ADD_DOCUMENT_SUCCESS);
                    refetch();
                    _reset();
                    setDocumentUrl("");
                    handleDocumentModalClose();
                    setDocumentsVal({});
                    setFileList([]);
                  },
                  onError: () => {
                    message.error(ADD_DOCUMENT_ERROR);
                  },
                },
              );
            }
            if (modalMode === ModalModes.EDIT) {
              await editDocument(
                {
                  section: section,
                  section_id: sectionId,
                  id: DatabaseSections.DOCUMENTS,
                  entityId: docId,
                  ..._formValues,
                  docAttachment: documentUrl,
                },
                {
                  onSuccess: () => {
                    message.success(EDIT_DOCUMENT_SUCCESS);
                    refetch();
                    _reset();
                    setDocumentUrl("");
                    handleDocumentModalClose();
                    setDocumentsVal({});
                    setFileList([]);
                    setModalMode(ModalModes.CREATE);
                  },
                  onError: () => {
                    message.error(ADD_DOCUMENT_ERROR);
                  },
                },
              );
            }
          };
          const handleCancel = () => {
            _reset();
            setDocumentUrl("");
            handleDocumentModalClose();
            setDocumentsVal({});
            setFileList([]);
            setModalMode(ModalModes.CREATE);
          };
          return (
            <Modal
              okText={SUBMIT_BUTTON_LABEL}
              okButtonProps={{
                loading:
                  modalMode === ModalModes.CREATE
                    ? isAddDocumentPending
                    : isEditDocumentPending,
              }}
              onOk={handleSubmit(handleAddDocument)}
              visible={isDocumentModalOpen}
              onCancel={handleCancel}
              title={
                modalMode === ModalModes.CREATE
                  ? ADD_DOCUMENT_MODAL_TITLE
                  : EDIT_DOCUMENT_MODAL_TITLE
              }
              width={WIDTH}
              maskClosable={false}
              destroyOnClose
            >
              <AddDocumentsForms
                setDocumentUrl={setDocumentUrl}
                fileList={fileList}
                setFileList={setFileList}
                isQuarryDoc={isQuarry}
                materials={materialData}
              />
            </Modal>
          );
        }}
      </Form>
      <CustomTableWithPagination
        columns={columns}
        data={data?.data}
        pageSize={pageSize}
        currentPage={currentPage}
        rowKey={(record) => record?.id}
        totalData={data?.meta?.totalRecords}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        loading={isFetching}
        className={styles.Table}
      />
      <TNButton
        text={ADD_DOCUMENT_LABEL}
        type={ButtonType.PRIMARY}
        size={ButtonSize.LARGE}
        shape={ButtonShape.DEFAULT}
        onClick={handleDocumentModalOpen}
        className={styles.addButton}
        disabled={!!blockedJob}
      />
    </>
  );
};

export default DocumentsTable;
